/**
 * P2P feature configuration + a production startup check.
 *
 * The `VITE_P2P_*_GROUP_ID` / `VITE_P2P_REQUESTS_MAILBOX` values are injected as
 * GitHub secrets in the SWA build. If a secret is missing they compile to `''`,
 * and `P2PRoleService.resolveRole()` then fails closed to `requester` for every
 * user — no privilege escalation, but designers/PMs/admins silently lose their
 * roles with no error or telemetry. `getP2PMailbox()` likewise falls back to a
 * hardcoded address. This surfaces that misconfiguration loudly at startup in
 * production instead of failing silently. (#273)
 */

/** Which P2P config inputs are missing. */
export interface P2PConfigStatus {
  /** Names of the empty `VITE_P2P_*_GROUP_ID` vars (RBAC disabled for these). */
  missingGroupIds: string[];
  /** True when `VITE_P2P_REQUESTS_MAILBOX` is unset (hardcoded fallback in use). */
  mailboxFallback: boolean;
}

export function getP2PConfigStatus(env: ImportMetaEnv = import.meta.env): P2PConfigStatus {
  const groups: Array<[string, string | undefined]> = [
    ['VITE_P2P_DESIGNERS_GROUP_ID', env.VITE_P2P_DESIGNERS_GROUP_ID],
    ['VITE_P2P_PMS_GROUP_ID', env.VITE_P2P_PMS_GROUP_ID],
    ['VITE_P2P_ADMINS_GROUP_ID', env.VITE_P2P_ADMINS_GROUP_ID],
  ];
  return {
    missingGroupIds: groups.filter(([, value]) => !value).map(([name]) => name),
    mailboxFallback: !env.VITE_P2P_REQUESTS_MAILBOX,
  };
}

interface WarnOptions {
  /** Defaults to `import.meta.env.PROD` (true only for production builds). */
  isProd?: boolean;
  /** Defaults to `import.meta.env`. */
  env?: ImportMetaEnv;
  /** Sink for the warning; defaults to `console.warn`. Injected for testing. */
  log?: (message: string) => void;
}

/**
 * In production builds, emit a single console warning when the P2P RBAC config
 * is incomplete. No-op in dev and when fully configured. Returns the warning
 * text (or `null`) so callers/tests can assert without capturing the console.
 */
export function warnIfP2PConfigIncompleteInProd(options: WarnOptions = {}): string | null {
  const isProd = options.isProd ?? import.meta.env.PROD;
  if (!isProd) return null;

  const status = getP2PConfigStatus(options.env);
  if (status.missingGroupIds.length === 0 && !status.mailboxFallback) return null;

  const parts: string[] = [];
  if (status.missingGroupIds.length) {
    parts.push(
      `missing group IDs [${status.missingGroupIds.join(', ')}] — all users resolve to "requester", P2P RBAC is disabled`,
    );
  }
  if (status.mailboxFallback) {
    parts.push('VITE_P2P_REQUESTS_MAILBOX unset — using the hardcoded fallback mailbox');
  }

  const message =
    `[P2P] Incomplete production configuration: ${parts.join('; ')}. ` +
    'Set the corresponding GitHub secret(s) in the SWA build workflow. (#273)';
  (options.log ?? console.warn)(message);
  return message;
}
